/** Roles and the capability matrix from tech spec §Team, roles and permissions. */
export const ROLES = ['owner', 'admin', 'editor', 'producer', 'viewer', 'client_reviewer'] as const;
export type Role = (typeof ROLES)[number];

export const CAPABILITIES = [
  'projects.view',
  'projects.manage',
  'customers.manage',
  'videos.edit',
  'ai.generate',
  'approve.internal',
  'approve.customer',
  'comment',
  'publish',
  'brandkits.edit_customer',
  'brandkits.edit_project',
  'members.manage',
  'billing.manage',
  /** Pair, rename and revoke studio computers running Castfeeds Engine; choose where sessions process. */
  'devices.manage',
  /** Custom domains, the white-label branding and the e-mail sender (plan §14). */
  'branding.manage',
  /** Connect, choose the target of, check and disconnect publishing integrations (amendment 20). */
  'integrations.manage',
  /** Tasks (plan §19.4): see the inbox and the activity feed; work on tasks; assign anyone; configure triggers. */
  'tasks.view',
  'tasks.work',
  'tasks.assign',
  'tasks.configure',
  /** Live Studio (WS W): see live sessions and their event log; run a session (scenes, graphics, markers, start/end). */
  'live.view',
  'live.operate',
] as const;
export type Capability = (typeof CAPABILITIES)[number];

const ALL: Capability[] = [...CAPABILITIES].filter((c) => c !== 'approve.customer');

const MATRIX: Record<Role, Capability[]> = {
  owner: ALL,
  admin: ALL.filter((c) => c !== 'billing.manage'),
  editor: ['projects.view', 'videos.edit', 'ai.generate', 'comment', 'brandkits.edit_project', 'tasks.view', 'tasks.work', 'live.view', 'live.operate'],
  producer: [
    'projects.view',
    'projects.manage',
    'customers.manage',
    'videos.edit',
    'ai.generate',
    'approve.internal',
    'comment',
    'publish',
    'brandkits.edit_customer',
    'brandkits.edit_project',
    'tasks.view',
    'tasks.work',
    'tasks.assign',
    'live.view',
    'live.operate',
  ],
  viewer: ['projects.view', 'comment', 'tasks.view', 'live.view'],
  client_reviewer: ['projects.view', 'approve.customer', 'comment'],
};

export function can(role: Role, capability: Capability): boolean {
  return MATRIX[role].includes(capability);
}

export function capabilitiesOf(role: Role): Capability[] {
  return [...MATRIX[role]];
}

/** Editors can spend on AI generation only within the workspace's per-job limit. */
export function canSpend(role: Role, costUsd: number, editorLimitUsd: number): boolean {
  if (!can(role, 'ai.generate')) return false;
  return role !== 'editor' || costUsd <= editorLimitUsd;
}

/** Who may assign which role: owners anything, admins anything except owner. */
export function canAssignRole(actor: Role, target: Role): boolean {
  if (actor === 'owner') return true;
  if (actor === 'admin') return target !== 'owner';
  return false;
}
